import { isFilled, asText as prismicHelperAsText } from '@prismicio/helpers';
import { LinkField, RichTextField, TitleField } from '@prismicio/client';
import { format, isValid, parse } from 'date-fns';
import { NavigationDocumentDataLinksItem } from 'prismicio-types';

type NavigationLinkVisibility = NonNullable<
  NavigationDocumentDataLinksItem['visibility']
>;
type NavigationViewport = Exclude<NavigationLinkVisibility, 'both'>;

type RichTextBlockType =
  | 'paragraph'
  | 'heading1'
  | 'heading2'
  | 'heading3'
  | 'heading4'
  | 'heading5'
  | 'heading6';

type LegacyRichTextValue = string | RichTextField | null | undefined;
const asText = (richText?: TitleField | RichTextField | null) => {
  if (!richText) {
    return '';
  }

  const array = Object.keys(richText).map(key => richText[key]) as TitleField;

  return prismicHelperAsText(array);
};

const convertToRichTextField = (
  text: string,
  type: RichTextBlockType = 'paragraph',
): RichTextField => [
  {
    type,
    text,
    spans: [],
  },
];

const normalizeToRichText = (
  value: LegacyRichTextValue,
  fallback: string,
  type: RichTextBlockType = 'heading1',
): RichTextField => {
  if (Array.isArray(value) && isFilled.richText(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    return convertToRichTextField(value.trim(), type);
  }

  return convertToRichTextField(fallback, type);
};

const shouldOpenLinkInNewTab = (
  externalLink: LinkField,
  externalNewTab: boolean,
): boolean => {
  if ('target' in externalLink && externalLink.target != null) {
    return externalLink.target === '_blank';
  }

  return externalNewTab;
};

const resolveLink = (
  internalLink?: string | null,
  externalLink?: LinkField | null,
  opts: Options = {},
): Resolved => {
  const { externalNewTab = true, downloadMedia = false } = opts;

  // 1) INTERNAL PRIORITY (KeyText)
  if (typeof internalLink === 'string') {
    const raw = internalLink.trim();
    if (raw) {
      const href =
        raw.startsWith('/') || raw.startsWith('#') || raw.startsWith('?')
          ? raw
          : `/${raw}`;

      return { kind: 'internal', props: { href } };
    }
  }

  // 2) EXTERNAL (LinkField)
  if (!isFilled.link(externalLink)) {
    return { kind: 'empty', props: { href: '' } };
  }

  // Media
  if (externalLink.link_type === 'Media') {
    const target: '_blank' | '_self' = shouldOpenLinkInNewTab(
      externalLink,
      externalNewTab,
    )
      ? '_blank'
      : '_self';
    const rel = target === '_blank' ? 'noopener noreferrer' : undefined;

    return {
      kind: 'media',
      props: {
        field: externalLink,
        target,
        rel,
        download: downloadMedia ? true : undefined,
      },
    };
  }

  // Document: respect per-link Prismic target (global externalNewTab does not apply)
  if (externalLink.link_type === 'Document') {
    const opensInNewTab =
      'target' in externalLink && externalLink.target === '_blank';

    return {
      kind: 'document',
      props: {
        field: externalLink,
        target: opensInNewTab ? '_blank' : '_self',
        rel: opensInNewTab ? 'noopener noreferrer' : undefined,
      },
    };
  }

  // Web (external): target/rel from global setting or per-link Prismic config
  const target: '_blank' | '_self' = shouldOpenLinkInNewTab(
    externalLink,
    externalNewTab,
  )
    ? '_blank'
    : '_self';
  const rel = target === '_blank' ? 'noopener noreferrer' : undefined;
  const webUrl = 'url' in externalLink ? externalLink.url : '';
  // Prepend https:// when the Web URL has no protocol (e.g. www.google.com).
  // Relative paths such as /about are left unchanged.
  const needsProtocol =
    typeof webUrl === 'string' &&
    !webUrl.startsWith('/') &&
    !/^https?:\/\//i.test(webUrl);
  const webField = needsProtocol
    ? { ...externalLink, url: `https://${webUrl}` }
    : externalLink;

  return {
    kind: 'external',
    props: {
      field: webField,
      target,
      rel,
    },
  };
};

type Resolved =
  | { kind: 'empty'; props: { href: '' } }
  | { kind: 'internal'; props: { href: string } }
  | {
      kind: 'external' | 'media' | 'document';
      props: {
        field: LinkField;
        target?: '_blank' | '_self';
        rel?: string;
        download?: boolean;
      };
    };

type Options = {
  externalNewTab?: boolean;
  downloadMedia?: boolean;
};

/** Prismic date fields are date-only (YYYY-MM-DD); parse as local calendar date. */
const parsePrismicDate = (date?: string | null): Date | null => {
  if (!date) {
    return null;
  }

  const parsed = parse(date, 'yyyy-MM-dd', new Date());

  return isValid(parsed) ? parsed : null;
};

const formatPrismicDate = (
  date?: string | null,
  formatStr = 'PPP',
): string | null => {
  const parsed = parsePrismicDate(date);

  return parsed ? format(parsed, formatStr) : null;
};

const filterNavigationLinksByVisibility = (
  links: NavigationDocumentDataLinksItem[] | null | undefined,
  viewport: NavigationViewport,
): NavigationDocumentDataLinksItem[] =>
  (links ?? []).filter(link => {
    const visibility: NavigationLinkVisibility = link.visibility ?? 'both';

    return visibility === 'both' || visibility === viewport;
  });

export {
  asText,
  convertToRichTextField,
  formatPrismicDate,
  normalizeToRichText,
  parsePrismicDate,
  resolveLink,
  filterNavigationLinksByVisibility,
};

export type { NavigationViewport, RichTextBlockType };
